import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Canonicalize an email for lookup/comparison: strip whitespace + lowercase.
 * Apply at every input boundary (forms, admin inputs).
 */
export const normalizeEmail = (email) => (email || '').trim().toLowerCase();

/**
 * Read the caller's Cognito group membership from the current session's ID token.
 *
 * NOTE: this is for UI gating only. Server-side authorization re-verifies
 * via a live Cognito query on every request — a tampered JWT gets through
 * the frontend guard but receives a 403 on every API call.
 *
 * @returns {Promise<{
 *   groups: string[],
 *   isFree: boolean,
 *   isPremium: boolean,
 *   isComp: boolean,
 *   isAdmin: boolean,
 *   isPro: boolean,
 * }>}
 */
export const checkUserGroup = async () => {
    try {
        const session = await fetchAuthSession();
        const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] || [];
        const isPremium = groups.includes('Premium');
        const isComp    = groups.includes('Comp');
        const isAdmin   = groups.includes('Admin');
        return {
            groups,
            isFree: groups.includes('Free'),
            isPremium,
            isComp,
            isAdmin,
            isPro: isPremium || isComp,
        };
    } catch (error) {
        console.error('Error fetching user groups', error);
        return { groups: [], isFree: false, isPremium: false, isComp: false, isAdmin: false, isPro: false };
    }
};

/**
 * Fetch that attaches a Cognito bearer token when the user is signed in, and
 * simply omits the Authorization header otherwise. Use for endpoints that are
 * publicly accessible but may personalize the response when auth is present.
 *
 * Returns the raw Response so callers can inspect status, ok, headers, etc.
 */
export const optionalAuthFetch = async (url, { method = 'GET', body } = {}) => {
    let idToken;
    try {
        const session = await fetchAuthSession();
        idToken = session?.tokens?.idToken?.toString();
    } catch {
        idToken = undefined;
    }

    const reqHeaders = { 'Content-Type': 'application/json' };
    if (idToken) reqHeaders.Authorization = `Bearer ${idToken}`;

    return fetch(url, {
        method,
        headers: reqHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });
};

/**
 * Authenticated fetch helper for admin API calls. Throws on non-2xx with the
 * server's error message when available.
 */
export const adminFetch = async (path, body = {}) => {
    const session = await fetchAuthSession();
    const idToken = session?.tokens?.idToken?.toString();
    if (!idToken) throw new Error('Not authenticated.');

    const resp = await fetch(`https://api.capitolgains.io${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.message || `Request failed (${resp.status})`);
    return data;
};
